/**
 * Configuration panel for the marketing report builder.
 *
 * Layout:
 *   Row 1: Breakdown | Date Preset
 *   Row 2: Chart Type | Interval | Cumulative toggle
 *   Row 3: Metrics selector (multi-select checkboxes)
 *   Row 4: Tiers
 *   Row 5: Filters (key=value pairs — includes traffic_source as a filter field)
 */
import { useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart, Table2, ChevronDown, ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { DatePresetPicker } from '@/components/filters/DatePresetPicker';
import { FilterBuilder } from '@/components/filters/FilterBuilder';
import { api } from '@/services/api';
import { endpoints } from '@/services/endpoints';
import {
  BREAKDOWN_OPTIONS,
  MARKETING_FILTER_FIELDS,
  MARKETING_FILTER_OPERATORS,
  MARKETING_FILTER_FIELD_META,
  getAvailableMetrics,
} from './marketing.constants';
import { INTERVAL_OPTIONS } from '@/sections/product/product.constants';
import { TierManager } from './TierManager';
import type { MarketingState } from './useMarketingState';
import type { DateRange } from '@/lib/dates';

interface MarketingConfigPanelProps {
  state: MarketingState;
  onChange: (patch: Partial<MarketingState>) => void;
  dates: DateRange;
}

export function MarketingConfigPanel({ state, onChange, dates }: MarketingConfigPanelProps) {
  const availableMetrics = getAvailableMetrics(state.breakdown);

  return (
    <div className="p-5 space-y-5">
      {/* Row 1: Breakdown / Date */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Select
          label="Row Breakdown"
          options={[...BREAKDOWN_OPTIONS]}
          value={state.breakdown}
          onChange={(e) => onChange({ breakdown: e.target.value })}
        />

        <DatePresetPicker
          value={state.preset}
          onChange={(preset) => onChange({ preset })}
          startDate={state.start}
          endDate={state.end}
          onDateChange={(start, end) => onChange({ start, end, preset: '' })}
          comparison={{
            preset: state.cmpPreset,
            start: state.cmpStart,
            end: state.cmpEnd,
            onPresetChange: (cmpPreset) => onChange({ cmpPreset, cmpStart: '', cmpEnd: '' }),
            onDateChange: (cmpStart, cmpEnd) => onChange({ cmpStart, cmpEnd, cmpPreset: '' }),
            onClear: () => onChange({ cmpPreset: '', cmpStart: '', cmpEnd: '' }),
            singleDate: state.chart === 'ts',
          }}
        />
      </div>

      {/* Row 2: Chart Type / Interval / Cumulative */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <ChartTypePicker
          value={state.chart}
          onChange={(chart) => onChange({ chart })}
        />

        {state.chart === 'ts' && (
          <Select
            label="Interval"
            options={[...INTERVAL_OPTIONS]}
            value={state.interval}
            onChange={(e) => onChange({ interval: e.target.value })}
          />
        )}

        {state.chart === 'ts' && (
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Cumulative</label>
            <button
              type="button"
              onClick={() => onChange({ cumulative: !state.cumulative })}
              className={cn(
                'h-9 px-4 rounded-lg border text-sm font-medium transition-colors',
                state.cumulative
                  ? 'bg-brand-50 border-brand-300 text-brand-700'
                  : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50',
              )}
            >
              {state.cumulative ? 'On' : 'Off'}
            </button>
          </div>
        )}
      </div>

      {/* Row 3: Metrics selector */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <label className="text-xs font-medium text-slate-600">Selected Metrics</label>
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => onChange({ metrics: availableMetrics.map((m) => m.value) })}
              className="text-2xs text-brand-600 hover:text-brand-800 font-medium"
            >
              Select All
            </button>
            <span className="text-2xs text-slate-300">|</span>
            <button
              type="button"
              onClick={() => onChange({ metrics: [] })}
              className="text-2xs text-slate-500 hover:text-slate-700 font-medium"
            >
              Deselect All
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {availableMetrics.map((m) => {
            const isSelected = state.metrics.includes(m.value);
            return (
              <button
                key={m.value}
                type="button"
                onClick={() => {
                  const next = isSelected
                    ? state.metrics.filter((x) => x !== m.value)
                    : [...state.metrics, m.value];
                  onChange({ metrics: next });
                }}
                className={cn(
                  'px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors',
                  isSelected
                    ? 'bg-brand-50 border-brand-300 text-brand-700'
                    : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50',
                )}
              >
                {m.label}
              </button>
            );
          })}
        </div>
        {state.metrics.length === 0 && (
          <p className="text-xs text-amber-600 mt-1">Select at least one metric</p>
        )}
      </div>

      {/* Row 4: Tiers */}
      <TierManager />

      {/* Row 5: Filters */}
      <FilterBuilder
        value={state.filters}
        onChange={(filters) => onChange({ filters })}
        fields={MARKETING_FILTER_FIELDS}
        operators={MARKETING_FILTER_OPERATORS}
        fieldMeta={MARKETING_FILTER_FIELD_META}
      />
    </div>
  );
}

// ─── Chart Type Picker ──────────────────────────────────────────────────────

const CHART_TYPES = [
  { value: 'ts' as const, label: 'Time Series', icon: LineChart },
  { value: 'pivot' as const, label: 'Pivot Table', icon: Table2 },
];

function ChartTypePicker({
  value,
  onChange,
}: {
  value: 'ts' | 'pivot';
  onChange: (v: 'ts' | 'pivot') => void;
}) {
  const [open, setOpen] = useState(false);

  const selected = CHART_TYPES.find((t) => t.value === value) || CHART_TYPES[0];
  const Icon = selected.icon;

  return (
    <div className="w-full">
      <label className="block text-xs font-medium text-slate-600 mb-1.5">Chart Type</label>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className={cn(
            'w-full h-9 rounded-lg border bg-white pl-3 pr-8 text-sm text-left',
            'flex items-center gap-2 cursor-pointer',
            'transition-colors duration-150',
            'focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400',
            open ? 'border-brand-400 ring-2 ring-brand-500/20' : 'border-slate-200 hover:border-slate-300',
          )}
        >
          <Icon className="w-4 h-4 text-brand-500 shrink-0" />
          <span className="truncate text-slate-900">{selected.label}</span>
          <ChevronDown className={cn(
            'absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 transition-transform duration-150',
            open && 'rotate-180',
          )} />
        </button>

        {open && (
          <>
            <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
            <div className="absolute z-30 top-full mt-1 left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-lg py-1 overflow-hidden">
              {CHART_TYPES.map((ct) => {
                const CtIcon = ct.icon;
                const isActive = ct.value === value;
                return (
                  <button
                    key={ct.value}
                    type="button"
                    onClick={() => {
                      onChange(ct.value);
                      setOpen(false);
                    }}
                    className={cn(
                      'w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors',
                      isActive
                        ? 'bg-brand-50 text-brand-700 font-medium'
                        : 'text-slate-700 hover:bg-slate-50',
                    )}
                  >
                    <CtIcon className={cn('w-4 h-4 shrink-0', isActive ? 'text-brand-500' : 'text-slate-400')} />
                    {ct.label}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

